// This config covers two fundamentally different oral products — Dukoral®
// (inactivated, oral suspension) and Vaxchora®▼ (live attenuated, oral
// solution, black triangle). Vaxchora® is a live vaccine; Dukoral® is not.
// Schedule selection is labelled by brand. The pharmacist selects the
// correct schedule for their chosen brand. requiresYellowCardDiscussion is
// set true conservatively because Vaxchora® is a black triangle product —
// the runner will display the Yellow Card discussion item for all cholera
// pathways.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.CHOLERA,
  displayName: "Oral cholera",
  serviceType: "administration",
  minimumAgeYears: 2,
  indicationSummary:
    "Travelling to or residing in an area where cholera vaccination is recommended by NaTHNaC, or a relief or aid worker in a cholera-affected area.",

  brands: [
    {
      brandId: "dukoral",
      brandName: "Dukoral\u00ae",
      minimumAgeYears: 2,
      route: "oral",
      doseDescription:
        "Oral suspension \u2014 see schedule for dose volume and preparation",
      administrationNotes:
        "For patients aged 6 years and over: dissolve the granules in approximately 150 mL of cool water; mix the effervescent granule buffer solution with the vaccine suspension and consume within 2 hours. For patients aged 2\u20135 years: discard approximately half of the buffer solution (approximately 75 mL) before mixing with the vaccine. The patient must have nothing to eat or drink, and must take no other oral medicines, for 1 hour before and 1 hour after each dose. The first dose is taken under supervision in the pharmacy.",
    },
    {
      brandId: "vaxchora",
      brandName: "Vaxchora\u00ae\u25bc",
      minimumAgeYears: 2,
      route: "oral",
      doseDescription: "Single oral dose \u2014 see schedule for preparation instructions",
      administrationNotes:
        "Mix the buffer component first, then add the active vaccine component; stir to mix. The patient must drink the entire contents within 15 minutes of preparation. The patient must have nothing to eat or drink for 60 minutes before and 60 minutes after taking the vaccine. Do not use if the foil pouch is damaged or if either component appears abnormal. Administer the full contents \u2014 if the patient vomits more than half the dose within 15 minutes, a replacement dose may be given within 72 hours.",
    },
  ],

  schedules: [
    {
      scheduleId: "cholera-dukoral-adult-primary",
      label:
        "Dukoral\u00ae \u2014 primary course, 6 years and over (2 doses, 1\u20136 weeks apart)",
      offLabel: false,
      minimumAgeYears: 6,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose (taken under supervision)",
        },
        {
          doseNumber: 2,
          label: "1\u20136 weeks after dose 1",
          intervalFromDose1Days: 14,
        },
      ],
      boosterInfo:
        "A single booster dose is recommended within 2 years of completing the primary course for patients at continued risk. If more than 2 years have elapsed, restart the primary course.",
      completionNotes:
        "If more than 6 weeks elapse between doses 1 and 2, restart the primary course from dose 1. Subsequent doses for home self-administration must be supplied with written preparation and storage instructions.",
    },
    {
      scheduleId: "cholera-dukoral-paediatric-primary",
      label:
        "Dukoral\u00ae \u2014 primary course, 2\u20135 years (3 doses, each 1\u20136 weeks apart)",
      offLabel: false,
      minimumAgeYears: 2,
      maximumAgeYears: 5,
      doses: [
        {
          doseNumber: 1,
          label:
            "Day 0 \u2014 first dose (taken under supervision; use approximately 75 mL buffer)",
        },
        {
          doseNumber: 2,
          label: "1\u20136 weeks after dose 1",
          intervalFromDose1Days: 14,
        },
        {
          doseNumber: 3,
          label: "1\u20136 weeks after dose 2",
          intervalFromDose1Days: 28,
        },
      ],
      boosterInfo:
        "A booster course (3 doses) is recommended within 6 months of completing the primary course for children aged 2\u20135 years. If more than 6 months have elapsed, restart the primary course.",
      completionNotes:
        "For children aged 2\u20135 years: use only approximately 75 mL of buffer solution (discard the rest before mixing). If more than 6 weeks elapse between any two doses, restart the primary course from dose 1.",
    },
    {
      scheduleId: "cholera-vaxchora-single",
      label:
        "Vaxchora\u00ae\u25bc \u2014 single dose, at least 10 days before potential exposure",
      offLabel: false,
      minimumAgeYears: 2,
      doses: [
        {
          doseNumber: 1,
          label:
            "Single dose \u2014 at least 10 days before entering a cholera-risk area",
        },
      ],
      completionNotes:
        "Vaxchora\u00ae is a single-dose schedule with no booster or follow-up dose. Advise the patient to wash hands after using the toilet and before handling food for at least 7 days after administration, due to possible vaccine strain shedding in approximately 11% of recipients.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "cholera-ex-anaphylaxis",
      question:
        "Has the patient had a confirmed anaphylactic reaction to a previous cholera vaccine or to any of its components (including formaldehyde residuals in Dukoral\u00ae)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Cholera vaccine is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
  ],

  cautions: [
    {
      checkId: "cholera-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "The safety of both Dukoral\u00ae and Vaxchora\u00ae\u25bc in pregnancy has not been established. Vaccination should proceed only where the risk of cholera infection is high and the benefit clearly outweighs any theoretical risk. Advise the patient to discuss with their GP or midwife. Note: Vaxchora\u00ae is a live vaccine \u2014 additional caution applies in pregnancy.",
    },
    {
      checkId: "cholera-caut-elderly",
      question: "Is the patient aged 65 or over?",
      cautionMessage:
        "Efficacy data for both cholera vaccines in patients aged 65 and over are limited. Counsel the patient that protection may be less reliable in this age group and that strict food and water hygiene remains essential.",
    },
    {
      checkId: "cholera-caut-immunosuppressed-dukoral",
      question: "Is the patient immunosuppressed? (Relevant if Dukoral\u00ae has been selected.)",
      cautionMessage:
        "Immunosuppression may reduce the immune response to Dukoral\u00ae. Vaccination should still proceed as the vaccine is inactivated and safe. Counsel that protection may be suboptimal and strict food and water hygiene remains essential.",
    },
    {
      checkId: "cholera-caut-vaxchora-shedding",
      question:
        "Does the patient live with or have close contact with infants, pregnant individuals, or immunocompromised people? (Relevant if Vaxchora\u00ae\u25bc has been selected.)",
      cautionMessage:
        "Vaxchora\u00ae\u25bc vaccine strain shedding occurs in approximately 11% of recipients. Advise the patient to wash hands thoroughly after using the toilet and before handling food for at least 7 days after vaccination, to protect close contacts who are infants, pregnant, or immunocompromised.",
    },
    {
      checkId: "cholera-caut-sodium",
      question: "Is the patient on a strictly controlled sodium diet?",
      cautionMessage:
        "Dukoral\u00ae contains approximately 1.1 g of sodium per dose. Vaxchora\u00ae\u25bc contains approximately 863 mg of sodium per dose. Advise patients on sodium-controlled diets to take this into account.",
    },
    {
      checkId: "cholera-caut-antibiotics-vaxchora",
      question:
        "Has the patient taken a systemic antibiotic within the last 14 days, or are they likely to start an antibiotic within 10 days after today? (Relevant if Vaxchora\u00ae\u25bc has been selected.)",
      cautionMessage:
        "Vaxchora\u00ae\u25bc should not be given within 14 days of systemic antibiotic use, and systemic antibiotics should be avoided for at least 10 days after Vaxchora\u00ae administration. If doxycycline for malaria prophylaxis is planned, ensure the 10-day gap after Vaxchora\u00ae is achievable before starting doxycycline. If the timing cannot be achieved, consider switching to Dukoral\u00ae (which is not affected by antibiotics).",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  // Vaxchora® is a black triangle product — Yellow Card discussion
  // applies if Vaxchora® was the selected product.
  requiresYellowCardDiscussion: true,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against cholera caused by Vibrio cholerae serogroup O1 only. It does not protect against all causes of travellers' diarrhoea or other gastrointestinal infections.",
    "Cholera vaccination reduces but does not eliminate risk. Continue to follow strict food and water hygiene: drink bottled or boiled water, avoid ice in drinks, eat freshly cooked food, and wash hands before eating and after using the toilet.",
    "If you received Dukoral\u00ae: do not eat, drink, or take any other oral medicine for 1 hour before and 1 hour after each dose. Store remaining doses in a refrigerator at 2\u20138\u00b0C and bring to room temperature before use.",
    "If you received Vaxchora\u00ae\u25bc: do not eat or drink for 60 minutes before and 60 minutes after taking the vaccine. Wash your hands thoroughly after using the toilet and before handling food for at least 7 days after vaccination.",
    "If you develop profuse watery diarrhoea, particularly with vomiting or rapid dehydration, during or after travel, seek urgent medical attention. Tell the doctor you may have been exposed to cholera.",
    "If Vaxchora\u00ae\u25bc was used: please report any suspected side effects to the MHRA via the Yellow Card scheme at yellowcard.mhra.gov.uk or by calling 0800 731 6789.",
  ],
});
